import { injectable, inject } from 'inversify';
import { LayoutableWindowState, LayoutableWindow } from './LayoutableWindow';
import { Layouter } from './Layouter';
import { Inject } from '../common/Injectables';
import { ServiceStore } from '../store/ServiceStore';
import { Debounce } from '../utils/Debounce';
import { LayoutStack } from './LayoutStack';
import { Toast } from '../model/Toast';

@injectable()
export class ToastStateHandler {
  private readonly _layouter: Layouter;
  private readonly _store: ServiceStore;
  private readonly _stack: LayoutStack;
  private readonly _checkQueueDebounce = new Debounce();

  private _checkQueueAwaiting = false;

  constructor(
    @inject(Inject.LAYOUTER) layouter: Layouter,
    @inject(Inject.STORE) store: ServiceStore,
    @inject(Inject.LAYOUT_STACK) stack: LayoutStack
  ) {
    this._layouter = layouter;
    this._store = store;
    this._stack = stack;
  }

  public async handleStateChange(
    win: LayoutableWindow,
    state: LayoutableWindowState
  ): Promise<void> {
    switch (state) {
      case LayoutableWindowState.QUEUED:
        this.handleQueuedState(win);
        break;
      case LayoutableWindowState.RENDERED:
        await this.handleRenderedState(win);
        break;
      case LayoutableWindowState.ACTIVE:
        await this.handleActiveState(win);
        break;
      case LayoutableWindowState.TRANSITION_OUT:
        this.handleTransitionOutState(win);
        break;
      case LayoutableWindowState.CLOSED:
        await this.handleClosedState(win);
        break;
    }
  }

  private handleQueuedState(win: LayoutableWindow) {
    if (this._stack.getToast(win.id)) {
      if (!this._store.state.centerVisible) {
        // Move toast to stack if it'll fit on screen. Otherwise, leave in queue.
        this.checkQueue();
      }
    } else {
      // Notification was deleted whilst window/toast was initialising
      console.info(`Toast created but no longer required (init) ${win.id}`);
      win.close();
    }
  }

  private async handleRenderedState(win: LayoutableWindow) {
    // Snap toast into starting position, and start transition-in animation
    await this._layouter.setInitialTransform(win);
    win.setState(LayoutableWindowState.ACTIVE); // setState is too complicated, might be a good idea to refactor it
  }

  private async handleActiveState(win: LayoutableWindow) {
    await win.show();
    this._layouter.layout(this._stack);
  }

  private handleTransitionOutState(win: LayoutableWindow) {
    // Queue-up a CLOSED transition once animation completes
    (win.currentTransition as Promise<void>).then(() => {
      win.setState(LayoutableWindowState.CLOSED);
    });
    this._layouter.layout(this._stack);
  }

  private async handleClosedState(win: LayoutableWindow) {
    // Keep toast in stack until window has fully closed
    // This ensures we won't attempt to create a new window with the same identity
    await win.close();
    this._stack.remove(win);
    // There is extra space now, check the queue.
    this.checkQueue();
  }

  /**
   * Adds a toast from awaiting toasts queue to the layout stack if it would fit.
   */
  public checkQueue(): void {
    if (this._checkQueueAwaiting) {
      return;
    }

    const fn = async () => {
      this._checkQueueAwaiting = true;

      if (this._layouter.layoutPromise) {
        await this._layouter.layoutPromise;
      }

      const items = this._layouter.getFittingItems(this._stack, this._stack.queue) as Toast[];

      await Promise.all(
        items.map(async (toast: LayoutableWindow) => {
          if (this._stack.moveToStack(toast)) {
            // Trigger rendering
            await toast.webWindow;
          } else {
            // Notification was deleted whilst toast was being measured and positioned
            console.info(`Toast created but no longer required (measure) ${toast.id}`);
            toast.close();
          }
        })
      );

      this._checkQueueAwaiting = false;
    };

    this._checkQueueDebounce.invoke(fn);
  }
}
